/**
 * Thin fetch wrapper around the /api/* backend.
 * Shares the same origin in prod (Workers Assets + api-worker) and proxies via
 * Vite in dev.
 */
import type {
  ProblemSummary,
  ProblemDetail,
  CreateSubmissionRequest,
  CreateSubmissionResponse,
  SubmissionDetail,
  SubmissionSummary,
  RegisterRequest,
  LoginRequest,
  AuthSuccessResponse,
  MeResponse,
  GetDraftResponse,
  SaveDraftRequest,
  Language,
  RunSamplesRequest,
  RunSamplesResponse,
  ProfileResponse,
  SiteStatsResponse,
} from '@flarecode/shared';

const BASE = '/api';

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    // Try to parse a structured error body {error: "code"}. Fall back to text.
    let code: string | undefined;
    let text = '';
    try {
      const body = await res.clone().json();
      if (body && typeof body === 'object' && typeof body.error === 'string') {
        code = body.error;
      }
    } catch {
      // not JSON
    }
    if (!code) {
      text = await res.text().catch(() => '');
    }
    throw new ApiError(res.status, code ?? text ?? res.statusText, code);
  }
  // 204 No Content doesn't have a body to parse.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Structured error from the backend. `code` is the machine-readable error
 * string (e.g. "invalid_credentials") when the server returned JSON of the
 * form {error: "..."}, otherwise undefined. Callers should switch on code
 * when available and fall back to status code / message otherwise.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
  ) {
    super(message);
  }
}

export const api = {
  // --- problems ---
  listProblems: () => request<{ problems: ProblemSummary[] }>('/problems'),
  getProblem: (slug: string) => request<{ problem: ProblemDetail }>(`/problems/${slug}`),

  // --- submissions ---
  createSubmission: (body: CreateSubmissionRequest) =>
    request<CreateSubmissionResponse>('/submissions', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  getSubmission: (id: string) => request<SubmissionDetail>(`/submissions/${id}`),
  listSubmissions: () => request<{ submissions: SubmissionSummary[] }>('/submissions'),
  runSamples: (body: RunSamplesRequest) =>
    request<RunSamplesResponse>('/submissions/run', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // --- auth ---
  me: () => request<MeResponse>('/auth/me'),
  register: (body: RegisterRequest) =>
    request<AuthSuccessResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  login: (body: LoginRequest) =>
    request<AuthSuccessResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  // --- drafts (per-user, per-problem, per-language) ---
  getDraft: (problemId: string, language: Language) =>
    request<GetDraftResponse>(`/drafts/${problemId}?language=${encodeURIComponent(language)}`),
  saveDraft: (problemId: string, body: SaveDraftRequest) =>
    request<void>(`/drafts/${problemId}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // --- user profile ---
  getProfile: (username: string) =>
    request<ProfileResponse>(`/users/${encodeURIComponent(username)}/profile`),

  // --- site-wide stats (homepage hero strip) ---
  getSiteStats: () => request<SiteStatsResponse>('/stats'),
};

/**
 * Build a WebSocket URL to the submission stream. Uses wss:// for https pages.
 */
export function submissionStreamUrl(submissionId: string): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/api/submissions/${submissionId}/stream`;
}
